<template>
  <component
    :is="resolveLayoutVariant"
    :class="`skin-variant--${appSkinVariant}`"
  >
    <LoadingOverlay :loading="loading" />
    <transition :name="appRouteTransition" mode="out-in" appear>
      <router-view></router-view>
    </transition>
  </component>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { computed } from "vue";
// eslint-disable-next-line import/no-unresolved
import useAppConfig from "@core/@app-config/useAppConfig";
import { useRouter } from "@core/utils";
import { useLayout } from "@core/layouts/composable/useLayout";

// Layouts
import LayoutContentVerticalNav from "@/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue";
import LayoutContentHorizontalNav from "@/layouts/variants/content/horizontal-nav/LayoutContentHorizontalNav.vue";
import LayoutBlank from "@/layouts/variants/blank/LayoutBlank.vue";

// Dynamic vh
import useDynamicVh from "@core/utils/useDynamicVh";

import LoadingOverlay from "@/components/LoadingOverlay";

import { mapActions, mapState } from "vuex";

import { setVeeLocale } from "@/utils";

import { invalidatePermissionsCache } from "@/router";

import CancelTripModal from "@/components/CancelTripModal.vue";
import CancelTripModalEN from "@/components/CancelTripModalEN.vue";

import Hotjar from "@hotjar/browser";

export default {
  components: {
    LayoutContentVerticalNav,
    LayoutContentHorizontalNav,
    LayoutBlank,
    LoadingOverlay,
  },
  setup() {
    const { route } = useRouter();
    const { appContentLayoutNav, appSkinVariant, appRouteTransition } =
      useAppConfig();

    const { handleBreakpointLayoutSwitch } = useLayout();
    handleBreakpointLayoutSwitch();

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === "blank") return "layout-blank";
      if (route.value.meta.layout === "content")
        return `layout-content-${appContentLayoutNav.value}-nav`;

      return null;
    });

    useDynamicVh();

    return {
      resolveLayoutVariant,
      appSkinVariant,
      appRouteTransition,
    };
  },
  computed: {
    ...mapState(["loading"]),
    ...mapState("auth", [
      "_id",
      "isLogged",
      "user",
      "email",
      "name",
      "unique_id",
    ]),
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        document.title =
          `${this.$t(to.meta.title)} | ${this.$t("Ridery Corporate")}` ||
          "Ridery Corporativo";
      },
    },
    user: {
      immediate: true,
      handler(newUser, oldUser) {
        if (newUser) {
          // Identificar al usuario en Hotjar
          Hotjar.identify(this.unique_id, {
            name: this.name,
            email: this.email,
            id: this.unique_id,
          });
          // Iniciar refresh periodico de permisos (cada 5 minutos)
          this.startPermissionsRefresh();
        } else {
          // Al hacer logout, detener el refresh periodico e invalidar cache
          this.stopPermissionsRefresh();
          invalidatePermissionsCache();
        }
      },
    },
    "$vuetify.lang.current": {
      immediate: true,
      handler(to, from) {
        document.title =
          `${this.$t(this.$route.meta.title)} | ${this.$t(
            "Ridery Corporate"
          )}` || "Ridery Corporativo";
      },
    },
  },
  data() {
    return {
      permissionsRefreshInterval: null,
    };
  },
  methods: {
    startPermissionsRefresh() {
      this.stopPermissionsRefresh();
      // Refrescar permisos cada 5 minutos mientras el usuario este autenticado
      this.permissionsRefreshInterval = setInterval(async () => {
        if (this.user) {
          try {
            await this.$store.dispatch("auth/refreshPermissions");
          } catch (_) {
            // Silenciar errores del refresh periodico para no interrumpir la UX
          }
        }
      }, 5 * 60 * 1000);
    },
    stopPermissionsRefresh() {
      if (this.permissionsRefreshInterval) {
        clearInterval(this.permissionsRefreshInterval);
        this.permissionsRefreshInterval = null;
      }
    },
    async toggleCancelTripModal(data) {
      const params = {
        fullscreen: this.$vuetify.breakpoint.smAndDown,
        width: "30%",
        transition: "dialog-top-transition",
        data: data,
        hideCancel: true,
        persistent: true,
      };

      if (
        this.$vuetify.lang.current === "es" &&
        this.$route.name !== "TripInfo"
      ) {
        const dialogInstance = await this.$dialog.showAndWait(
          CancelTripModal,
          params
        );
        if (!dialogInstance) {
          dialogInstance.close();
        }
      }
      if (this.$route.name !== "TripInfo") {
        const dialogInstance = await this.$dialog.showAndWait(
          CancelTripModalEN,
          params
        );
        if (!dialogInstance) {
          dialogInstance.close();
        }
      }
    },
  },
  sockets: {
    connect: function () {
      console.info("socket connected");
    },
    CANCEL_TRIP_CORPORATE(data) {
      let corp_id = data.corporate_id;
      if (this.isLogged && corp_id === this._id) {
        this.toggleCancelTripModal(data);
        console.info(data);
      }
    },
  },
  beforeMount() {
    setVeeLocale(this.$i18n.locale);
  },
  created() {
    // new DevicePixelRatio().init()
  },
  beforeDestroy() {
    this.stopPermissionsRefresh();
  },
};
</script>

<style>
::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}
::-webkit-scrollbar {
  background-color: white;
}
::-webkit-scrollbar-thumb {
  background-color: #d8d8d8;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  /* background-color: #d8d8d8; */
}
::-webkit-scrollbar-thumb:active {
  /* background-color: #d8d8d8; */
}
::-webkit-scrollbar-corner {
  background-color: #d8d8d8;
}

::-webkit-scrollbar-track {
  border-radius: 10px;
}

.no-transform {
  text-transform: none !important;
}

.w-60 {
  width: 60%;
}

.h-full {
  height: 100%;
}
</style>
